import { Fragment } from 'react';
import { MdExpandMore, MdInfoOutline } from 'react-icons/md';

import { ButtonOutlined } from '@/components/Button';
import { PendingBadge } from '@/components/PendingBadge/PendingBadge';
import { formatRole } from '@/components/Widget/WhosOnBoardWidget/WhosOnBoardWidget.constants';
import type { PastGuest } from '@/store/events/events.mappers';
import { isOwnerGroupRole } from '@/utils/roles';

import { AUDIENCE_OPTIONS } from '../eventWizard.constants';
import type { CrewMember, Guest } from '../eventWizard.types';
import s from '../EventWizardModal.module.scss';

import { InvitePanel } from './InvitePanel';

interface StepAudienceProps {
  crewMembers: CrewMember[];
  /** Owners and crew who will be on the event; the rest are left out of it. */
  attendingIds: string[];
  /** On create, the backend seats every owner and crew member but those left out. */
  creating: boolean;
  /** Someone who cannot be left out — the creator, on create. */
  lockedId: string | null;
  onToggleTeamMember: (id: string) => void;
  onAddAllTeam: () => void;
  teamOpen: boolean;
  onToggleTeamOpen: () => void;
  guestsOpen: boolean;
  onToggleGuestsOpen: () => void;
  guests: Guest[];
  onRemoveGuest: (id: number) => void;
  /** People who were guests at this vessel's past events — invited by person id. */
  pastGuests: PastGuest[];
  pastGuestsSelected: string[];
  onTogglePastGuest: (personId: string) => void;
  onClearInvite: () => void;
  inviteName: string;
  onInviteNameChange: (value: string) => void;
  inviteContact: string;
  onInviteContactChange: (value: string) => void;
  onSendInvitation: (picked: { name: string; contact: string; personId?: string }[]) => void;
}

export function StepAudience({
  crewMembers,
  attendingIds,
  creating,
  lockedId,
  onToggleTeamMember,
  onAddAllTeam,
  teamOpen,
  onToggleTeamOpen,
  guestsOpen,
  onToggleGuestsOpen,
  guests,
  onRemoveGuest,
  pastGuests,
  pastGuestsSelected,
  onTogglePastGuest,
  onClearInvite,
  inviteName,
  onInviteNameChange,
  inviteContact,
  onInviteContactChange,
  onSendInvitation,
}: StepAudienceProps) {
  // Owners and co-owners are seated like the crew, but they are not crew: they host the
  // event, while the crew works it. Neither is invited — only guests are.
  const owners = crewMembers.filter((m) => isOwnerGroupRole(m.role));
  const crew = crewMembers.filter((m) => !isOwnerGroupRole(m.role));

  const renderMember = (m: CrewMember) => {
    const attending = attendingIds.includes(m.id);
    const locked = m.id === lockedId;
    const content = (
      <>
        <div className={s.personAvatar}>{m.name.charAt(0)}</div>
        {locked ? (
          // The backend always seats whoever creates the event (not necessarily its
          // organiser) — say why their row cannot be tapped.
          <div className={s.personInfo}>
            <span className={s.personName}>{m.name} (you)</span>
            <span className={s.personRole}>
              You&apos;re the creator of this event, so you&apos;re always on it
            </span>
          </div>
        ) : (
          <span className={s.personName}>{m.name}</span>
        )}
        <span className={s.personRole}>{formatRole(m.role)}</span>
        <span className={s.personToggle}>{attending ? '✓' : '+'}</span>
      </>
    );
    if (locked) {
      return (
        <div key={m.id} className={`${s.personRow} ${s.personRowSelected}`}>
          {content}
        </div>
      );
    }
    return (
      <button
        key={m.id}
        type="button"
        className={`${s.personRow} ${s.personRowSelectable} ${attending ? s.personRowSelected : ''}`}
        onClick={() => onToggleTeamMember(m.id)}
        aria-pressed={attending}
      >
        {content}
      </button>
    );
  };

  const crewCount = crewMembers.filter((m) => attendingIds.includes(m.id)).length;
  const selectedPastCount = pastGuests.filter((g) =>
    pastGuestsSelected.includes(g.personId),
  ).length;
  // One person, one invitation: whichever list picked them first locks them out of the other.
  const inGuestList = guests.flatMap((g) => g.personId ?? []);
  const unavailable = Object.fromEntries([
    ...pastGuestsSelected.map((id) => [id, 'Selected from recent events']),
    ...inGuestList.map((id) => [id, 'Already in guest list']),
  ]);

  return (
    <>
      <div className={s.stepHeader}>
        <h3 className={s.stepTitle}>Who is this event for?</h3>
        <p className={s.stepSubtitle}>
          {creating
            ? 'The owners and crew join automatically. You can invite guests too.'
            : 'Please add or remove owners and crew, or invite guests.'}
        </p>
      </div>
      <div className={s.fields}>
        {AUDIENCE_OPTIONS.map((opt) => {
          const open = opt.id === 'team' ? teamOpen : guestsOpen;
          const toggle = opt.id === 'team' ? onToggleTeamOpen : onToggleGuestsOpen;
          const count = opt.id === 'team' ? crewCount : guests.length + selectedPastCount;
          return (
            <div key={opt.id} className={s.audienceCard}>
              <button
                type="button"
                className={s.audienceHeader}
                onClick={toggle}
                aria-expanded={open}
              >
                <span className={s.optionEmoji}>{opt.emoji}</span>
                <span className={s.audienceHeaderText}>
                  <span className={s.optionLabel}>{opt.label}</span>
                  <span className={s.optionDesc}>{opt.desc}</span>
                </span>
                {count > 0 && <span className={s.audienceCount}>{count}</span>}
                <MdExpandMore
                  className={`${s.audienceChevron} ${open ? s.audienceChevronOpen : ''}`}
                />
              </button>

              {open && opt.id === 'team' && (
                <div className={s.audienceBody}>
                  {creating && (
                    <div className={s.crewAutoNote}>
                      <MdInfoOutline />
                      The owners and every crew member join this event automatically. Please tap
                      anyone who won’t be there. Guests join only when you invite them.
                    </div>
                  )}
                  <div className={s.crewHeader}>
                    <div className={s.subSectionTitle}>{crewCount} on this event</div>
                    <ButtonOutlined
                      onClick={onAddAllTeam}
                      disabled={crewCount === crewMembers.length}
                    >
                      + Add all owners &amp; crew
                    </ButtonOutlined>
                  </div>
                  {crewMembers.length === 0 && (
                    <span className={s.personRole}>
                      Nobody’s aboard yet. Please add crew to the vessel first.
                    </span>
                  )}
                  {[
                    { title: 'Owners', members: owners },
                    { title: 'Crew', members: crew },
                  ].map(
                    (group) =>
                      group.members.length > 0 && (
                        <Fragment key={group.title}>
                          <div className={s.subSectionTitle}>
                            {group.title} ({group.members.length})
                          </div>
                          <div className={s.rowList}>{group.members.map(renderMember)}</div>
                        </Fragment>
                      ),
                  )}
                </div>
              )}

              {open && opt.id === 'guests' && (
                <div className={s.audienceBody}>
                  <InvitePanel
                    name={inviteName}
                    onNameChange={onInviteNameChange}
                    contact={inviteContact}
                    onContactChange={onInviteContactChange}
                    onClear={onClearInvite}
                    onSend={onSendInvitation}
                    unavailable={unavailable}
                  />

                  {pastGuests.length > 0 && (
                    <>
                      <div className={s.subSectionTitle}>
                        From recent events ({selectedPastCount} selected)
                      </div>
                      <div className={s.rowList}>
                        {pastGuests.map((g) => {
                          const selected = pastGuestsSelected.includes(g.personId);
                          const locked = inGuestList.includes(g.personId);
                          return (
                            <button
                              key={g.personId}
                              type="button"
                              className={`${s.personRow} ${s.personRowSelectable} ${selected ? s.personRowSelected : ''} ${locked ? s.personRowLocked : ''}`}
                              onClick={() => onTogglePastGuest(g.personId)}
                              aria-pressed={selected}
                              disabled={locked}
                            >
                              <div className={s.personAvatar}>{g.name.charAt(0).toUpperCase()}</div>
                              <span className={s.personName}>{g.name}</span>
                              <span className={s.personRole}>{g.lastEventTitle}</span>
                              {locked ? (
                                <span className={s.personLockedPill}>In guest list</span>
                              ) : (
                                <span className={s.personToggle}>{selected ? '✓' : '+'}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* The heading names the list, so it sits on it and waits for the first guest. */}
                  {guests.length > 0 && (
                    <>
                      <div className={s.subSectionTitle}>
                        Guest list <span className={s.listCount}>({guests.length})</span>
                      </div>
                      <div className={s.rowList}>
                        {guests.map((guest) => (
                          <div key={guest.id} className={s.personRow}>
                            <div className={s.personAvatar}>
                              {guest.name.charAt(0).toUpperCase()}
                            </div>
                            <div className={s.personInfo}>
                              <span className={s.personName}>{guest.name}</span>
                              <span className={s.personRole}>{guest.contact}</span>
                            </div>
                            <PendingBadge />
                            <button
                              type="button"
                              className={s.removeBtn}
                              onClick={() => onRemoveGuest(guest.id)}
                              aria-label={`Remove ${guest.name}`}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
